import { PanelProps } from '@blueprintjs/core';
import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { sendHubspotEvent, usePortalFeatures } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { MAX_CSV_UPLOAD_BYTES } from 'src/constants/limits';
import { ProjectFileUploadPanel } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadPanel';
import { useAutomaticallySetHeadersOption } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/hooks';
import { ProjectFileUploadPanelFile } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/types';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { useCreateMappings } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCreateMappings';
import { useFetchMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useFetchMappingSuggestions';
import { projectSelector } from 'src/selectors/project';
import {
  ProjectFileUpload,
  ProjectFileUploadOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { unparseCsv } from 'src/utils/csv/unparseCsv';
import { generateDateRandomId } from 'src/utils/generateId';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useProjectMultipleFileUploadsParser } from 'src/utils/hooks/useProjectFileUploadParser';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { readFile } from 'src/utils/readFile';
import { useEvaluateSummary } from 'src/utils/voicebox/hooks/useEvaluateSummary';

export type ProjectFileUploadCreationPanelProps =
  ProjectResourceDialogPanelProps & {
    uploadedFiles: File[];
  };

export const ProjectFileUploadCreationPanel: React.VFC<
  PanelProps<ProjectFileUploadCreationPanelProps>
> = ({ closePanel, setFooterProps, uploadedFiles }) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const dispatch = useReduxDispatch();
  const { evaluateSummary } = useEvaluateSummary();
  const isMounted = useIsMounted();
  const portalFeatures = usePortalFeatures();
  const useVoiceboxSuggestions = Boolean(
    useIsVoiceboxEnabled() && portalFeatures.voiceboxSuggestions
  );

  const [files, setFiles] = React.useState<ProjectFileUploadPanelFile[]>([]);
  const [fileOptions, setFileOptions] =
    React.useState<ProjectFileUploadOptions>(defaultProjectFileUploadOptions);
  const [selectedTab, setSelectedTab] = React.useState(0);

  const { isThereDuplicateHeaders } = useAutomaticallySetHeadersOption({
    files: uploadedFiles,
    fileOptions,
    setFileOptions,
  });
  const {
    abortParserFns,
    isParsing,
    errorsByFileIdx: parserErrorsByFileIdx,
    fieldsByFileIdx,
    rowsByFileIdx,
    updateFieldLabelForFileIdx,
  } = useProjectMultipleFileUploadsParser(
    files.map((file) => file.file), // parse after useEffect goes through
    fileOptions,
    MAX_CSV_UPLOAD_BYTES
  );
  const currentFields = React.useMemo(
    () => fieldsByFileIdx[selectedTab] || [],
    [fieldsByFileIdx, selectedTab]
  );

  const fileOptionsError = React.useMemo(() => {
    if (!fileOptions.separator) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.separator;
    }
    if (!fileOptions.quote) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.quote;
    }
    if (!fileOptions.escape) {
      return copy.components.projectResourceDialog.projectFileUploadPanel
        .validation.escape;
    }
    return '';
  }, [fileOptions]);
  const errorMessagesByIdx = React.useMemo(() => {
    return files.reduce<Record<number, string>>((acc, file, idx) => {
      const tabName =
        files[idx].fileName ||
        copy.components.projectResourceDialog.projectFileUploadPanel.form.namelessTabLabel(
          idx + 1
        );
      if (fileOptionsError) {
        acc[idx] =
          copy.components.projectResourceDialog.projectFileUploadPanel.validation.errorWithName(
            fileOptionsError,
            tabName
          );
      } else if (parserErrorsByFileIdx[idx]?.length) {
        acc[idx] =
          copy.components.projectResourceDialog.projectFileUploadPanel.validation.errorWithName(
            copy.components.projectResourceDialog.projectFileUploadPanel
              .validation.errors,
            tabName
          );
      } else if (!rowsByFileIdx[idx]?.length) {
        acc[idx] =
          copy.components.projectResourceDialog.projectFileUploadPanel.validation.errorWithName(
            copy.components.projectResourceDialog.projectFileUploadPanel
              .validation.empty,
            tabName
          );
      } else if (!file.fileName) {
        acc[idx] =
          copy.components.projectResourceDialog.projectFileUploadPanel.validation.errorWithName(
            copy.components.projectResourceDialog.projectFileUploadPanel
              .validation.name,
            tabName
          );
      } else {
        acc[idx] = '';
      }
      return acc;
    }, {});
  }, [files, fileOptionsError, parserErrorsByFileIdx, rowsByFileIdx]);
  const buttonErrorText = React.useMemo(() => {
    if (errorMessagesByIdx[selectedTab]) {
      return errorMessagesByIdx[selectedTab];
    }
    const otherTabError = Object.values(errorMessagesByIdx).find(
      (message) => message.length
    );

    return otherTabError || '';
  }, [errorMessagesByIdx, selectedTab]);

  const isOpen = useReduxSelector(
    (state) => state.ui.isProjectResourceDialogOpen
  );
  const didIsOpenChange = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (didIsOpenChange && !isOpen) {
      abortParserFns.forEach((abortParser) => abortParser());
      setFiles([]);
    }
  }, [abortParserFns, didIsOpenChange, isOpen]);

  const createFileUploads = React.useCallback(() => {
    const filesToUpload: Array<Omit<ProjectFileUpload, 'dateLastUpdated'>> =
      files.map((file, idx) => ({
        name: file.fileName,
        path: file.filePath,
        contents: file.isFilePartial
          ? unparseCsv({
              fields: fieldsByFileIdx[idx].map(({ value }) => value),
              rows: rowsByFileIdx[idx],
              fileOptions,
            })
          : file.fileContents,
        options: fileOptions,
        fields: fieldsByFileIdx[idx],
        // TODO: determine how to de-dupe files with the same 'path'
        // Due to browser security we can't get the full file path, only the name
        // and extension, so treat uploads as unique and give them a uuid for now.
        // In the future we'll need some handler for recognising when files are
        // uploaded with the same path, and a choice to clone or replace the data.
        id: generateDateRandomId(),
        partial: file.isFilePartial,
      }));
    dispatch(
      projectsActionCreators.addedFileUploadsToProject(projectId, filesToUpload)
    );

    return filesToUpload.map((file) => file.id);
  }, [dispatch, fieldsByFileIdx, fileOptions, files, projectId, rowsByFileIdx]);

  const handleCreateButtonClick = React.useCallback(() => {
    const fileUploadIds = createFileUploads();
    if (fileUploadIds.length) {
      sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_CSV);
      dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
    }
  }, [createFileUploads, dispatch]);

  const { fetchMappingSuggestions, isLoading } = useFetchMappingSuggestions();
  const handleCreateMappings = useCreateMappings();

  const handleCreateAndMapButtonClick = React.useCallback(async () => {
    const fileUploadIds = createFileUploads();
    if (fileUploadIds.length) {
      sendHubspotEvent(TrackingEventList.DESIGNER_DEFINE_RESOURCE_CSV);
      const loaded = await fetchMappingSuggestions({
        selectedClasses: [],
        selectedResources: fileUploadIds,
      });
      if (loaded.mappingSuggestions?.length) {
        await handleCreateMappings(loaded.mappingSuggestions);
      }
      if (useVoiceboxSuggestions) {
        evaluateSummary(project);
      }
      dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: false }));
    }
  }, [
    createFileUploads,
    dispatch,
    project,
    fetchMappingSuggestions,
    handleCreateMappings,
    useVoiceboxSuggestions,
  ]);

  const handleSetCurrentFileName = React.useCallback(
    (name: string) => {
      const nextFiles = files.map<ProjectFileUploadPanelFile>((file, idx) => ({
        ...file,
        fileName: selectedTab === idx ? name : file.fileName,
      }));
      setFiles(nextFiles);
    },
    [files, selectedTab]
  );

  const handleSetCurrentFileFields = React.useCallback(
    (value: string, label: string) => {
      updateFieldLabelForFileIdx(selectedTab, value, label);
    },
    [selectedTab, updateFieldLabelForFileIdx]
  );

  const handleCloseFileTab = (closeIndex: number) => {
    // if last tab is closed (move to tab before closed one),
    // OR the closed tab is before the current tab (stay on same tab)
    if (selectedTab === files.length - 1 || closeIndex < selectedTab) {
      setSelectedTab(selectedTab - 1);
    }
    const newFiles = [...files];
    newFiles.splice(closeIndex, 1);

    setFiles(newFiles);
  };

  React.useEffect(() => {
    if (isMounted.current) {
      setFooterProps({
        createAndMapButtonPending: isLoading,
        createButtonsError: buttonErrorText,
        handleBackButtonClick: closePanel,
        handleCreateAndMapButtonClick,
        handleCreateButtonClick,
      });
    }
  }, [
    buttonErrorText,
    closePanel,
    handleCreateAndMapButtonClick,
    handleCreateButtonClick,
    isLoading,
    isMounted,
    setFooterProps,
  ]);

  React.useEffect(() => {
    let mounted = true;

    if (mounted && uploadedFiles?.length) {
      let areAnyFilesPartial = false;
      const filesToSet: ProjectFileUploadPanelFile[] = [];
      const fileContentPromises: Array<Promise<string>> = [];
      uploadedFiles.forEach((uploadedFile) => {
        const isFilePartial = uploadedFile.size > MAX_CSV_UPLOAD_BYTES;
        if (isFilePartial) {
          areAnyFilesPartial = true;
        }

        const fileContentsPromise = isFilePartial
          ? Promise.resolve('')
          : readFile(uploadedFile);
        const file: ProjectFileUploadPanelFile = {
          fileName: uploadedFile.name.replace(/\.[^/.]+$/, ''),
          filePath: uploadedFile.name,
          isFilePartial,
          fileContents: '',
          file: uploadedFile,
          id: uuid(),
        };
        fileContentPromises.push(fileContentsPromise);
        filesToSet.push(file);
      });
      if (areAnyFilesPartial) {
        dispatch(projectsActionCreators.uploadedPartialCsv());
      }

      Promise.all(fileContentPromises)
        .then((fileContentsArr) => {
          fileContentsArr.forEach((fileContents, idx) => {
            filesToSet[idx].fileContents = fileContents;
          });
        })
        .then(() => {
          setFiles(filesToSet);
        });
    } else {
      setFiles([]);
    }

    return () => {
      mounted = false;
    };
  }, [dispatch, uploadedFiles]);

  return (
    <ProjectFileUploadPanel
      closeTab={handleCloseFileTab}
      fields={currentFields}
      fileOptions={fileOptions}
      files={files}
      isParsing={isParsing}
      isThereDuplicateHeaders={isThereDuplicateHeaders}
      parserErrorsByFileIdx={parserErrorsByFileIdx}
      rowsByFileIdx={rowsByFileIdx}
      selectedTab={selectedTab}
      setFields={handleSetCurrentFileFields}
      setFileName={handleSetCurrentFileName}
      setFileOptions={setFileOptions}
      setSelectedTab={setSelectedTab}
    />
  );
};
